/**
 * Centralized date formatting utilities for consistent date/time display
 * throughout the Fleet application.
 *
 * Format standard: MM/DD/YYYY at HH:MM (24-hour clock)
 */

/**
 * Formats a date as MM/DD/YYYY
 * @param date - Date string or Date object
 * @returns Formatted date string (e.g., "01/15/2025")
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${month}/${day}/${year}`;
}

/**
 * Formats a date and time as MM/DD/YYYY at HH:MM
 * @param date - Date string or Date object
 * @returns Formatted date and time string (e.g., "01/15/2025 at 14:30")
 */
export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const datePart = formatDate(dateObj);
  const timePart = formatTime(dateObj);

  return `${datePart} at ${timePart}`;
}

/**
 * Formats time as HH:MM (24-hour format)
 * @param date - Date string or Date object
 * @returns Formatted time string (e.g., "14:30")
 */
export function formatTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Time';
  }

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

/**
 * Formats relative time for recent dates (< 24 hours) or absolute date/time for older dates
 * @param date - Date string or Date object
 * @returns Formatted relative or absolute time string
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const diffInHours = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    return formatDateTime(dateObj);
  }
}

/**
 * Formats a status time with relative time for recent dates or short date format for older dates
 * @param date - Date string or Date object or undefined
 * @returns Formatted status time string or "Unknown" if date is invalid/missing
 */
export function formatStatusTime(date?: string | Date): string {
  if (!date) return 'Unknown';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  if (isNaN(dateObj.getTime())) {
    return 'Unknown';
  }

  const diffInMinutes = (now.getTime() - dateObj.getTime()) / (1000 * 60);

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    const minutes = Math.floor(diffInMinutes);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInMinutes < 1440) { // Less than 24 hours
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInMinutes < 10080) { // Less than 7 days
    const days = Math.floor(diffInMinutes / 1440);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateObj);
  }
}

/**
 * Formats a timestamp for "Last updated" display
 * @param date - Date string or Date object or undefined
 * @returns Formatted last updated string (e.g., "Last updated: 8/29/2025 8:03AM")
 */
export function formatLastUpdated(date?: string | Date): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours();
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  return `Last updated: ${month}/${day}/${year} ${displayHours}:${minutes}${ampm}`;
}

/**
 * Formats current time for a specific timezone offset with optional DST indicator
 * @param timezoneOffsetMinutes - UTC offset in minutes (e.g., -300 for UTC-5, 60 for UTC+1)
 * @param isDaylightSavings - Whether daylight saving time is in effect
 * @param currentTime - Optional specific time to format (defaults to current time)
 * @returns Formatted time string with timezone (e.g., "2:30 PM UTC-5 (DST)" or "14:30 UTC+1")
 */
export function formatTimeWithTimezone(
  timezoneOffsetMinutes: number,
  isDaylightSavings: boolean = false,
  currentTime?: Date
): string {
  try {
    // Use provided time or current time
    const now = currentTime || new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const timezoneTime = new Date(utcTime.getTime() + (timezoneOffsetMinutes * 60000));

    // Format the time part
    const timeOptions: Intl.DateTimeFormatOptions = {hour: 'numeric', minute: '2-digit', hour12: true};

    const timeString = timezoneTime.toLocaleTimeString('en-US', timeOptions);

    // Create timezone label
    const offsetHours = timezoneOffsetMinutes / 60;
    const sign = timezoneOffsetMinutes >= 0 ? '+' : '';
    const tzLabel = `UTC${sign}${offsetHours}${isDaylightSavings ? ' (DST)' : ''}`;

    return `${timeString} ${tzLabel}`;
  } catch (error) {
    console.warn('Error formatting timezone time:', error);
    return 'Invalid Time';
  }
}